import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Spin, message, Progress, Alert, Tabs,
  Button, Modal, Form, Input, InputNumber, Select, Space,
} from 'antd';
import ReactEChartsCore from 'echarts-for-react';
import { carbon, carbonLedger } from '../../../services/api';

const SCOPE_COLORS = { '范围一': 'red', '范围二': 'orange', '范围三': 'blue' };
const TYPE_META = {
  green_power: { label: '绿电', color: 'green' },
  charging: { label: '充电替代', color: 'blue' },
  storage: { label: '储能', color: 'gold' },
};
const RIGHT_STATUS = ['未登记', '绿证已核发', '已交易', 'CCER候选'];
const ASSET_TYPES = ['绿证', '碳配额', 'CCER'];

export default function CarbonPage() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [targets, setTargets] = useState(null);
  const [assets, setAssets] = useState(null);
  const [footprint, setFootprint] = useState(null);

  // 碳模块 v1：台账 / 因子 / 资产登记
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [factors, setFactors] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [factorOpen, setFactorOpen] = useState(false);
  const [editingFactor, setEditingFactor] = useState(null);
  const [manualForm] = Form.useForm();
  const [assetForm] = Form.useForm();
  const [factorForm] = Form.useForm();

  const loadSummary = useCallback(() => {
    carbonLedger.summary().then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const loadLedger = useCallback((page = 1) => {
    setLedgerLoading(true);
    carbonLedger.list({ page, pageSize: 20 })
      .then((r) => { setLedger(r.data.list); setLedgerTotal(r.data.total); setLedgerPage(page); })
      .catch(() => message.warning('台账加载失败'))
      .finally(() => setLedgerLoading(false));
  }, []);

  const loadFactors = useCallback(() => {
    carbonLedger.factors().then((r) => setFactors(r.data)).catch(() => {});
  }, []);

  const loadAssets = useCallback(() => {
    return carbon.assets().then((r) => setAssets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.allSettled([
      carbon.overview(),
      carbon.trends({ months: 12 }),
      carbon.breakdown(),
      carbon.targets(),
      loadAssets(),
      carbon.footprint(),
    ])
      .then((results) => {
        const [ov, tr, bd, tg, , fp] = results;
        if (ov.status === 'fulfilled') setOverview(ov.value.data);
        else message.warning('能碳概览加载失败');
        if (tr.status === 'fulfilled') setTrends(tr.value.data);
        if (bd.status === 'fulfilled') setBreakdown(bd.value.data);
        if (tg.status === 'fulfilled') setTargets(tg.value.data);
        if (fp.status === 'fulfilled') setFootprint(fp.value.data);
      })
      .finally(() => setLoading(false));
    loadSummary();
    loadLedger(1);
    loadFactors();
  }, [loadAssets, loadSummary, loadLedger, loadFactors]);

  const trendOption = useMemo(() => {
    if (!trends?.months) return {};
    const months = trends.months.map((m) => m.month.slice(5));
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['总排放', '清洁能源减碳', '净排放'] },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: months },
      yAxis: { type: 'value', name: 'kg CO₂' },
      series: [
        { name: '总排放', type: 'bar', stack: 'total', data: trends.months.map((m) => m.gross_emission_kg), itemStyle: { color: '#ff7875' } },
        { name: '清洁能源减碳', type: 'bar', data: trends.months.map((m) => -m.clean_reduction_kg), itemStyle: { color: '#52c41a' } },
        { name: '净排放', type: 'line', data: trends.months.map((m) => m.net_emission_kg), itemStyle: { color: '#1890ff' } },
      ],
    };
  }, [trends]);

  const breakdownPie = useMemo(() => {
    if (!breakdown?.emission_sources) return {};
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} kg ({d}%)' },
      series: [{
        type: 'pie', radius: ['42%', '68%'],
        data: breakdown.emission_sources.map((s) => ({
          name: s.source,
          value: s.emission_kg,
        })),
        label: { formatter: '{b}\n{d}%' },
      }],
    };
  }, [breakdown]);

  const ledgerChart = useMemo(() => {
    if (!summary?.by_month?.length) return {};
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: summary.by_month.map((m) => m.month) },
      yAxis: { type: 'value', name: 'tCO₂e' },
      series: [{ name: '减排量', type: 'bar', data: summary.by_month.map((m) => m.reduction_tco2e), itemStyle: { color: '#52c41a' } }],
    };
  }, [summary]);

  const handleSync = () => {
    setSyncing(true);
    carbonLedger.sync()
      .then((r) => {
        const d = r.data;
        message.success(`核算完成：新增 ${d.created} 条，重复 ${d.duplicated} 条，净替代因子 ${d.net_factor} kgCO₂/kWh`);
        loadSummary();
        loadLedger(1);
      })
      .catch((e) => message.error(e.response?.data?.message || '核算同步失败'))
      .finally(() => setSyncing(false));
  };

  const handleManualSubmit = () => {
    manualForm.validateFields().then((values) => {
      carbonLedger.create(values)
        .then(() => {
          message.success('台账已录入');
          setManualOpen(false);
          manualForm.resetFields();
          loadSummary();
          loadLedger(1);
        })
        .catch((e) => message.error(e.response?.data?.message || '录入失败'));
    });
  };

  const handleAssetSubmit = () => {
    assetForm.validateFields().then((values) => {
      const call = editingAsset
        ? carbonLedger.updateAsset(editingAsset.id, values)
        : carbonLedger.createAsset(values);
      call
        .then(() => {
          message.success(editingAsset ? '资产已更新' : '资产已登记');
          setAssetOpen(false);
          setEditingAsset(null);
          assetForm.resetFields();
          loadAssets();
        })
        .catch((e) => message.error(e.response?.data?.message || '保存失败'));
    });
  };

  const handleFactorSubmit = () => {
    factorForm.validateFields().then((values) => {
      carbonLedger.updateFactor(editingFactor.key, values.value)
        .then(() => {
          message.success(`因子已调整为 ${values.value}`);
          setFactorOpen(false);
          setEditingFactor(null);
          loadFactors();
        })
        .catch((e) => message.error(e.response?.data?.message || '调整失败'));
    });
  };

  const targetColumns = [
    { title: '月份', dataIndex: 'month', render: (m) => `${m}月` },
    { title: '减排目标(kg)', dataIndex: 'target_reduction_kg', render: (v) => v.toLocaleString() },
    { title: '实际减排(kg)', dataIndex: 'actual_reduction_kg', render: (v) => <span style={{ color: '#52c41a' }}>{v.toLocaleString()}</span> },
    {
      title: '完成率',
      dataIndex: 'achievement_rate',
      render: (v) => <Progress percent={Math.min(v, 100)} size="small" status={v >= 100 ? 'success' : 'active'} />,
    },
    { title: '状态', dataIndex: 'status', render: (s) => <Tag color={s === '达标' ? 'green' : 'orange'}>{s}</Tag> },
  ];

  const ledgerColumns = [
    { title: '归属日期', dataIndex: 'period_date', width: 110 },
    { title: '项目', dataIndex: 'project_name', ellipsis: true },
    {
      title: '类型', dataIndex: 'reduction_type', width: 100,
      render: (t) => { const m = TYPE_META[t] || { label: t, color: 'default' }; return <Tag color={m.color}>{m.label}</Tag>; },
    },
    { title: '电量(kWh)', dataIndex: 'energy_kwh', width: 110, render: (v) => parseFloat(v).toLocaleString() },
    { title: '折算因子', dataIndex: 'factor_used', width: 90, render: (v, r) => <span title={r.factor_note}>{v}</span> },
    { title: '减排量(tCO₂e)', dataIndex: 'reduction_tco2e', width: 120, render: (v) => <span style={{ color: '#52c41a' }}>{parseFloat(v).toFixed(4)}</span> },
    { title: '权益状态', dataIndex: 'right_status', width: 100, render: (s) => <Tag>{s}</Tag> },
    {
      title: '溯源', dataIndex: 'source_id', width: 90,
      render: (s, r) => (r.source_type === 'manual' ? <Tag>手工</Tag> : <span style={{ fontSize: 12, color: '#888' }} title={s}>{String(s || '').slice(-8)}</span>),
    },
  ];

  const assetColumns = [
    { title: '类型', dataIndex: 'asset_type', render: (t) => <Tag color={t === '绿证' ? 'green' : t === 'CCER' ? 'blue' : 'gold'}>{t}</Tag> },
    { title: '名称', dataIndex: 'name', ellipsis: true },
    { title: '数量', render: (_, r) => `${parseFloat(r.quantity).toLocaleString()} ${r.unit}` },
    { title: '单价(元)', dataIndex: 'unit_price', render: (v) => (v != null ? parseFloat(v).toLocaleString() : '-') },
    { title: '估值(元)', dataIndex: 'value_yuan', render: (v) => (v != null ? v.toLocaleString() : '-') },
    { title: '状态', dataIndex: 'status', render: (s) => <Tag color={s === '持有' ? 'green' : s === '已交易' ? 'blue' : 'default'}>{s}</Tag> },
    { title: '凭证号', dataIndex: 'voucher_no', render: (v) => v || '-' },
    { title: '登记日期', dataIndex: 'acquired_date', render: (v) => v || '-' },
    {
      title: '操作', width: 70,
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => { setEditingAsset(r); assetForm.setFieldsValue(r); setAssetOpen(true); }}>编辑</Button>
      ),
    },
  ];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!overview) return <div>暂无数据</div>;

  const progressColor = overview.target_progress_percent >= 100 ? '#52c41a' : overview.target_progress_percent >= 80 ? '#faad14' : '#ff4d4f';
  const assetList = Array.isArray(assets) ? assets : [];
  const assetTotalValue = assetList.reduce((s, a) => s + (a.value_yuan || 0), 0);

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="能碳管理"
        description={`园区碳排放监测、减排目标考核与碳资产管理。电网排放因子 ${overview.factor} ${overview.factor_unit}，对标 ISO 14064 与园区 ESG 披露。`}
      />

      <Row gutter={[16, 16]}>
        <Col span={4}><Card size="small"><Statistic title="本月总排放" value={overview.gross_emission_kg} suffix="kg" precision={0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="清洁能源减碳" value={overview.clean_reduction_kg} suffix="kg" precision={0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="本月净排放" value={overview.net_emission_kg} suffix="kg" precision={0} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="碳强度" value={overview.emission_intensity} suffix={overview.intensity_unit} precision={4} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="年减排进度" value={overview.target_progress_percent} suffix="%" precision={1} valueStyle={{ color: progressColor }} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="等效植树" value={overview.equivalent_trees} suffix="棵" valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={16}>
          <Card title="月度碳排放趋势（近12月）" size="small">
            <ReactEChartsCore option={trendOption} style={{ height: 300 }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="排放来源构成" size="small">
            <ReactEChartsCore option={breakdownPie} style={{ height: 300 }} />
          </Card>
        </Col>
      </Row>

      <Tabs
        style={{ marginTop: 16 }}
        defaultActiveKey="ledger"
        items={[
          {
            key: 'ledger',
            label: '碳资产台账',
            children: (
              <div>
                <Row gutter={[16, 16]}>
                  <Col span={6}><Card size="small"><Statistic title="累计减排量" value={summary?.total_reduction_tco2e ?? 0} suffix="tCO₂e" precision={4} valueStyle={{ color: '#52c41a' }} /></Card></Col>
                  <Col span={6}><Card size="small"><Statistic title="台账记录" value={summary?.record_count ?? 0} suffix="条" /></Card></Col>
                  <Col span={6}><Card size="small"><Statistic title="累计电量依据" value={summary?.total_energy_kwh ?? 0} suffix="kWh" precision={0} /></Card></Col>
                  <Col span={6}><Card size="small"><Statistic title="等效植树" value={summary?.equivalent_trees ?? 0} suffix="棵" valueStyle={{ color: '#52c41a' }} /></Card></Col>
                </Row>

                {summary?.by_month?.length > 0 && (
                  <Card title="月度减排趋势" size="small" style={{ marginTop: 16 }}>
                    <ReactEChartsCore option={ledgerChart} style={{ height: 240 }} />
                  </Card>
                )}

                <Card
                  title="减排台账（每条可溯源）"
                  size="small"
                  style={{ marginTop: 16 }}
                  extra={(
                    <Space>
                      <Button size="small" onClick={() => setManualOpen(true)}>手工录入</Button>
                      <Button size="small" type="primary" loading={syncing} onClick={handleSync}>立即核算</Button>
                    </Space>
                  )}
                >
                  {ledger.length === 0 && !ledgerLoading ? (
                    <Alert
                      type="info"
                      showIcon
                      message="暂无台账数据"
                      description="充电交易记录推送到达后，点击右上角「立即核算」自动生成充电替代燃油减排台账；绿证核发等线下权益可点「手工录入」登记。"
                    />
                  ) : (
                    <Table
                      size="small"
                      loading={ledgerLoading}
                      columns={ledgerColumns}
                      dataSource={ledger.map((r) => ({ ...r, key: r.id }))}
                      pagination={{
                        current: ledgerPage,
                        total: ledgerTotal,
                        pageSize: 20,
                        onChange: loadLedger,
                        showTotal: (t) => `共 ${t} 条`,
                      }}
                    />
                  )}
                </Card>

                <Card title="折算因子（方法学明确后可调）" size="small" style={{ marginTop: 16 }}>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={factors.map((f) => ({ ...f, key: f.key }))}
                    columns={[
                      { title: '因子', dataIndex: 'key' },
                      { title: '当前值', dataIndex: 'value' },
                      { title: '口径说明', dataIndex: 'description', ellipsis: true },
                      {
                        title: '操作', width: 70,
                        render: (_, f) => (
                          <Button type="link" size="small" onClick={() => { setEditingFactor(f); factorForm.setFieldsValue({ value: parseFloat(f.value) }); setFactorOpen(true); }}>调整</Button>
                        ),
                      },
                    ]}
                  />
                </Card>
              </div>
            ),
          },
          {
            key: 'assets',
            label: '资产登记',
            children: (
              <Card
                size="small"
                extra={<Button size="small" type="primary" onClick={() => { setEditingAsset(null); assetForm.resetFields(); setAssetOpen(true); }}>新增资产</Button>}
              >
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={6}><Statistic title="登记资产" value={assetList.length} suffix="项" /></Col>
                  <Col span={6}><Statistic title="持有中" value={assetList.filter((a) => a.status === '持有').length} suffix="项" valueStyle={{ color: '#52c41a' }} /></Col>
                  <Col span={6}><Statistic title="总估值" value={assetTotalValue} suffix="元" precision={2} /></Col>
                </Row>
                {assetList.length === 0 ? (
                  <Alert type="info" showIcon message="暂无登记资产" description="绿证核发、CCER 签发热度达成后，点右上角「新增资产」登记持有量与凭证号。" />
                ) : (
                  <Table
                    columns={assetColumns}
                    dataSource={assetList.map((a) => ({ ...a, key: a.id }))}
                    pagination={false}
                    size="small"
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'targets',
            label: '减排目标考核',
            children: targets ? (
              <Card size="small">
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={6}><Statistic title={`${targets.year}年减排目标`} value={targets.annual_reduction_target_kg} suffix="kg" /></Col>
                  <Col span={6}><Statistic title="累计实际减排" value={targets.ytd_actual_reduction_kg} suffix="kg" valueStyle={{ color: '#52c41a' }} /></Col>
                  <Col span={6}><Statistic title="年度完成率" value={targets.ytd_achievement_rate} suffix="%" valueStyle={{ color: progressColor }} /></Col>
                  <Col span={6}><Statistic title="绿证目标" value={targets.green_cert_target} suffix="张" /></Col>
                </Row>
                <Table
                  columns={targetColumns}
                  dataSource={targets.monthly.map((m) => ({ ...m, key: m.month }))}
                  pagination={false}
                  size="small"
                />
              </Card>
            ) : null,
          },
          {
            key: 'breakdown',
            label: '排放与减排明细',
            children: breakdown ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Card title="排放来源（范围一/二）" size="small">
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={breakdown.emission_sources.map((s) => ({ ...s, key: s.source }))}
                      columns={[
                        { title: '来源', dataIndex: 'source' },
                        { title: '范围', dataIndex: 'scope', render: (s) => <Tag color={SCOPE_COLORS[s]}>{s}</Tag> },
                        { title: '排放(kg)', dataIndex: 'emission_kg', render: (v) => v.toLocaleString() },
                        { title: '占比', dataIndex: 'ratio', render: (v) => `${v}%` },
                      ]}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="清洁能源减排贡献" size="small">
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={breakdown.reduction_sources.map((s) => ({ ...s, key: s.source }))}
                      columns={[
                        { title: '来源', dataIndex: 'source' },
                        { title: '类型', dataIndex: 'type', render: () => <Tag color="green">减排</Tag> },
                        { title: '减碳量(kg)', dataIndex: 'amount_kg', render: (v) => <span style={{ color: '#52c41a' }}>{v.toLocaleString()}</span> },
                      ]}
                    />
                    <div style={{ marginTop: 12, textAlign: 'right', color: '#666' }}>
                      净排放：<b>{breakdown.net_kg.toLocaleString()}</b> kg CO₂
                    </div>
                  </Card>
                </Col>
              </Row>
            ) : null,
          },
          {
            key: 'footprint',
            label: '碳足迹（范围一/二/三）',
            children: footprint ? (
              <Card size="small">
                <Table
                  size="small"
                  pagination={false}
                  dataSource={footprint.scopes.map((s) => ({ ...s, key: s.scope }))}
                  columns={[
                    { title: '范围', dataIndex: 'scope', render: (s) => <Tag color={SCOPE_COLORS[s]}>{s}</Tag> },
                    { title: '类别', dataIndex: 'name' },
                    { title: '包含项', dataIndex: 'items', render: (items) => items.join('、') },
                    { title: '排放(kg)', dataIndex: 'emission_kg', render: (v) => v.toLocaleString() },
                    { title: '占比', dataIndex: 'ratio', render: (v) => `${v}%` },
                  ]}
                />
                <div style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
                  核算方法：{footprint.methodology} · 合计 {footprint.total_kg.toLocaleString()} kg CO₂
                </div>
              </Card>
            ) : null,
          },
        ]}
      />

      <Modal
        title="手工录入台账"
        open={manualOpen}
        onOk={handleManualSubmit}
        onCancel={() => setManualOpen(false)}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={manualForm} layout="vertical" initialValues={{ reduction_type: 'green_power', right_status: '未登记' }}>
          <Form.Item name="project_name" label="项目名称" rules={[{ required: true, message: '请填写项目名称' }]}>
            <Input placeholder="如：玉舍充电站光伏" />
          </Form.Item>
          <Form.Item name="period_date" label="归属日期" rules={[{ required: true, message: '格式 YYYY-MM-DD' }, { pattern: /^\d{4}-\d{2}-\d{2}$/, message: '格式 YYYY-MM-DD' }]}>
            <Input placeholder="2026-10-01" />
          </Form.Item>
          <Form.Item name="reduction_type" label="减排类型" rules={[{ required: true }]}>
            <Select options={Object.entries(TYPE_META).map(([v, m]) => ({ value: v, label: m.label }))} />
          </Form.Item>
          <Form.Item name="energy_kwh" label="电量依据(kWh)" rules={[{ required: true, message: '请填写电量' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="factor_used" label="折算因子(kgCO₂/kWh，留空取电网因子)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="right_status" label="权益状态">
            <Select options={RIGHT_STATUS.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="value_yuan" label="记价(元，可空)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingAsset ? '编辑资产' : '新增资产'}
        open={assetOpen}
        onOk={handleAssetSubmit}
        onCancel={() => { setAssetOpen(false); setEditingAsset(null); }}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={assetForm} layout="vertical" initialValues={{ asset_type: '绿证', status: '持有' }}>
          <Form.Item name="asset_type" label="资产类型" rules={[{ required: true }]}>
            <Select options={ASSET_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请填写名称' }]}>
            <Input placeholder="如：2026年玉舍光伏绿证（第一批）" />
          </Form.Item>
          <Form.Item name="quantity" label="数量" rules={[{ required: true, message: '请填写数量' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="unit" label="单位（绿证=张，其他=吨）">
            <Input placeholder="张 / 吨" />
          </Form.Item>
          <Form.Item name="unit_price" label="单价(元，可空)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={['持有', '已交易', '已注销'].map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="acquired_date" label="登记日期" rules={[{ pattern: /^\d{4}-\d{2}-\d{2}$/, message: '格式 YYYY-MM-DD' }]}>
            <Input placeholder="2026-10-01" />
          </Form.Item>
          <Form.Item name="voucher_no" label="凭证号">
            <Input />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`调整因子：${editingFactor?.key || ''}`}
        open={factorOpen}
        onOk={handleFactorSubmit}
        onCancel={() => { setFactorOpen(false); setEditingFactor(null); }}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="因子影响后续核算结果，调整会记录操作日志；已生成的台账保留调整前的因子快照。" />
        <Form form={factorForm} layout="vertical">
          <Form.Item name="value" label={editingFactor?.description || '新值'} rules={[{ required: true, message: '请填写非负数字' }]}>
            <InputNumber min={0} step={0.0001} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
